// WorkspaceMenu.tsx — the ONE control at the bottom of the rail / bottom of the mobile drawer
// (Johnny, dev review 2026-08-21, on top of the workspace-first shell spec: two separate
// controls — a workspace switcher pinned to the top and a person-only account menu pinned to
// the bottom — read as two systems when there is only one identity worth surfacing per screen.
// Merges the old top-of-rail workspace switcher and the old bottom-of-rail person-only account
// menu (both deleted) into a single trigger and a single menu, workspace items first, then a
// labelled account group.
//
// The menu body is exported separately from its trigger (`WorkspaceMenuContent` vs
// `WorkspaceMenuRow`) so `MobileTopBar`'s avatar — a second, top-bar-anchored entry point into
// this exact menu — can reuse the body verbatim with its own trigger and popover placement,
// same reasoning the old account menu's own content export used to carry.
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import {
  ChevronsUpDownIcon,
  CreditCardIcon,
  LanguagesIcon,
  LogOutIcon,
  MonitorIcon,
  MoonIcon,
  SettingsIcon,
  ShieldIcon,
  SunIcon,
  UserIcon,
  UsersIcon,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@repo/ui/components/dropdown-menu';
import type { MemberRole } from '@repo/shared-types';
import { useBillingSummary } from '../../hooks/useBillingSummary';
import { useMyWorkspaces } from '../../hooks/useMyWorkspaces';
import { getThemePreference, setThemePreference, type ThemePreference } from '../../lib/theme';
import { WORKSPACE_KEY } from '../../lib/landing';
import { useLocale } from '../../locales/context';
import type { Catalog } from '../../locales/en';
import { LOCALE_LABEL, LOCALE_PREFERENCES, type LocalePreference } from '../../locales';
import { WorkspaceAvatar } from './WorkspaceAvatar';

/** Locale-dashboard plan, Task 4: values moved to `t.shell.role*` keys — built from `t` rather
 * than a module-level constant. */
function roleLabel(t: Catalog, role: MemberRole): string {
  const map: Record<MemberRole, string> = {
    owner: t.shell.roleOwner,
    admin: t.shell.roleAdmin,
    member: t.shell.roleMember,
  };
  return map[role];
}

// Cycle order: follow the OS until the user takes a side. Carried over from the old account menu.
const NEXT_THEME: Record<ThemePreference, ThemePreference> = {
  system: 'dark',
  dark: 'light',
  light: 'system',
};
/** Locale-dashboard plan, Task 4: values moved to `t.shell.theme*` keys. */
function themeLabel(t: Catalog, pref: ThemePreference): string {
  const map: Record<ThemePreference, string> = {
    system: t.shell.themeSystem,
    dark: t.shell.themeDark,
    light: t.shell.themeLight,
  };
  return map[pref];
}
const THEME_ICON: Record<ThemePreference, typeof MonitorIcon> = {
  system: MonitorIcon,
  dark: MoonIcon,
  light: SunIcon,
};

// Locale-more-languages cycle: the Language row is now a submenu listing System + every
// supported locale (`LOCALE_PREFERENCES`, `../../locales`), not a click-to-cycle item — nine
// real locales made "keep clicking to find yours" impractical (it was fine for a 3-state
// cycle). `LOCALE_LABEL`'s entries are endonyms and are NOT catalog keys — "Tiếng Việt" reads
// as Vietnamese to a Vietnamese speaker whatever the UI language currently is; "System" stays a
// plain, un-translated label (pre-existing behavior, not something this cycle changes).
const PREFERENCE_LABEL: Record<LocalePreference, string> = { system: 'System', ...LOCALE_LABEL };

const TRIGGER_CLASS: Record<'rail' | 'drawer', string> = {
  rail: 'flex h-12 w-full shrink-0 min-w-0 items-center gap-2 rounded-lg px-1.5 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35',
  drawer:
    'flex min-h-[50px] w-full shrink-0 min-w-0 items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35',
};

function selectWorkspace(id: string) {
  localStorage.setItem(WORKSPACE_KEY, id);
  // Hard reload, not navigate(): every query keyed without a workspace id would otherwise keep
  // resolving against the previous workspace (same rationale as SignIn.tsx's post-auth redirect).
  location.assign('/');
}

/** The menu body — workspace group, then a labelled account group — independent of whatever
 * triggers it. Each mount owns its own theme-preference `useState`, same as the old account
 * menu's content export used to: two independent triggers (the rail/drawer row and the top bar's avatar) mean two
 * independent bits of "which icon am I showing" state, both reading the same
 * `getThemePreference()` source of truth. */
export function WorkspaceMenuContent({
  email,
  isAdmin,
  onSignOut,
  onNavigate,
  align,
  side,
  sideOffset = 8,
}: {
  email: string;
  isAdmin: boolean;
  onSignOut: () => void;
  /** Drawer passes its `onClose` so a menu link also closes the drawer. */
  onNavigate?: () => void;
  align: 'start' | 'end';
  side: 'top' | 'bottom';
  sideOffset?: number;
}) {
  const { data: workspaces } = useMyWorkspaces();
  const { data: billing } = useBillingSummary();
  const [pref, setPref] = useState<ThemePreference>(getThemePreference);
  const ThemeIcon = THEME_ICON[pref];
  const { preference, setPreference, t } = useLocale();

  function cycleTheme() {
    const next = NEXT_THEME[pref];
    setThemePreference(next);
    setPref(next);
  }

  if (!workspaces || workspaces.length === 0) return null;

  const active = workspaces.find((w) => w.isActive) ?? workspaces[0]!;
  const canManage = active.role === 'owner' || active.role === 'admin';
  const subline = t.shell.workspaceSubline({
    plan: billing?.plan.name ?? null,
    role: roleLabel(t, active.role),
  });

  return (
    <DropdownMenuContent align={align} side={side} sideOffset={sideOffset} className="w-72">
      <DropdownMenuLabel className="pb-0 font-semibold">{active.name}</DropdownMenuLabel>
      <p className="px-2 pb-1.5 text-[12px] text-muted-foreground">{subline}</p>
      <DropdownMenuSeparator />
      {/* The management group is all-or-nothing by role, so its trailing separator is gated
          with it — a member would otherwise see two rules back to back. */}
      {canManage && (
        <DropdownMenuItem asChild>
          <Link to="/workspace" onClick={onNavigate}>
            <SettingsIcon />
            {t.shell.workspaceSettings}
          </Link>
        </DropdownMenuItem>
      )}
      {canManage && (
        <DropdownMenuItem asChild>
          <Link to="/members" onClick={onNavigate}>
            <UsersIcon />
            {t.shell.members}
          </Link>
        </DropdownMenuItem>
      )}
      {canManage && (
        <DropdownMenuItem asChild>
          <Link to="/billing" onClick={onNavigate}>
            <CreditCardIcon />
            {t.shell.billingUsage}
          </Link>
        </DropdownMenuItem>
      )}
      {canManage && <DropdownMenuSeparator />}
      <DropdownMenuLabel className="text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
        {t.shell.switchWorkspace}
      </DropdownMenuLabel>
      {workspaces.map((w) => (
        <DropdownMenuCheckboxItem
          key={w.id}
          checked={w.isActive}
          onSelect={() => {
            if (!w.isActive) selectWorkspace(w.id);
          }}
        >
          <WorkspaceAvatar name={w.name} id={w.id} size={20} logoUrl={w.logoUrl} />
          <span className="min-w-0 flex-1 truncate">{w.name}</span>
          <span className="text-[11.5px] text-muted-foreground">{roleLabel(t, w.role)}</span>
        </DropdownMenuCheckboxItem>
      ))}
      <DropdownMenuSeparator />
      <DropdownMenuLabel className="text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
        {t.shell.accountLabelPrefix}
        <span className="normal-case">{email}</span>
      </DropdownMenuLabel>
      <DropdownMenuItem asChild>
        <Link to="/account" onClick={onNavigate}>
          <UserIcon />
          {t.shell.account}
        </Link>
      </DropdownMenuItem>
      <DropdownMenuItem
        onSelect={(e) => {
          // Keep the menu open so the mode can be cycled again without a re-open.
          e.preventDefault();
          cycleTheme();
        }}
      >
        <ThemeIcon />
        {t.shell.themePrefix}
        {themeLabel(t, pref)}
        <DropdownMenuShortcut>
          {t.shell.nextPrefix}
          {themeLabel(t, NEXT_THEME[pref])}
        </DropdownMenuShortcut>
      </DropdownMenuItem>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <LanguagesIcon />
          {t.shell.languagePrefix}
          {PREFERENCE_LABEL[preference]}
        </DropdownMenuSubTrigger>
        <DropdownMenuPortal>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup
              value={preference}
              onValueChange={(value) => setPreference(value as LocalePreference)}
            >
              {LOCALE_PREFERENCES.map((localePref) => (
                <DropdownMenuRadioItem key={localePref} value={localePref}>
                  {PREFERENCE_LABEL[localePref]}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuPortal>
      </DropdownMenuSub>
      {isAdmin && (
        <DropdownMenuItem asChild>
          <Link to="/admin" onClick={onNavigate}>
            <ShieldIcon />
            {t.shell.platformAdmin}
          </Link>
        </DropdownMenuItem>
      )}
      <DropdownMenuSeparator />
      <DropdownMenuItem variant="destructive" onSelect={onSignOut}>
        <LogOutIcon />
        {t.shell.signOut}
      </DropdownMenuItem>
    </DropdownMenuContent>
  );
}

export function WorkspaceMenuRow({
  email,
  isAdmin,
  onSignOut,
  onNavigate,
  touch = false,
}: {
  email: string;
  isAdmin: boolean;
  onSignOut: () => void;
  /** Drawer passes its `onClose` so a menu link also closes the drawer. */
  onNavigate?: () => void;
  /** The drawer's larger touch target vs the rail's fixed 48px row — a prop, not a fork
   * (same idiom the old account row used for its own `touch` prop). */
  touch?: boolean;
}) {
  const { data: workspaces } = useMyWorkspaces();
  const { t } = useLocale();
  const variant = touch ? 'drawer' : 'rail';

  // Stale-selection guard — carried over from the old workspace switcher unchanged.
  const guardRan = useRef(false);
  useEffect(() => {
    if (!workspaces || guardRan.current) return;
    const stored = localStorage.getItem(WORKSPACE_KEY);
    if (!stored) return;
    if (workspaces.some((w) => w.id === stored)) return;
    guardRan.current = true;
    localStorage.removeItem(WORKSPACE_KEY);
    location.assign('/');
  }, [workspaces]);

  if (!workspaces || workspaces.length === 0) {
    return <div className={`${TRIGGER_CLASS[variant]} pointer-events-none`} aria-hidden="true" />;
  }

  const active = workspaces.find((w) => w.isActive) ?? workspaces[0]!;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t.shell.workspaceMenuAriaLabel({ name: active.name })}
          className={TRIGGER_CLASS[variant]}
        >
          <WorkspaceAvatar name={active.name} id={active.id} size={touch ? 30 : 28} logoUrl={active.logoUrl} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-semibold">{active.name}</span>
            <span className="block truncate text-[11.5px] text-muted-foreground">{email}</span>
          </span>
          <ChevronsUpDownIcon size={13} strokeWidth={2.2} className="shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <WorkspaceMenuContent
        email={email}
        isAdmin={isAdmin}
        onSignOut={onSignOut}
        onNavigate={onNavigate}
        align="start"
        side="top"
      />
    </DropdownMenu>
  );
}
